import React, { useState, useEffect } from 'react';
import {
  Box, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, TextField, Stack, Button, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import { useCalculateMoneyContext } from '../../../context/CalculateMoneyContext';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';

import OverviewTodayViewStaff from './views/OverviewTodayViewStaff';
import OverviewDayViewStaff from './views/OverviewDayViewStaff';
import OverviewWeekView from '../../CalculateMoney/views/OverviewWeekView';
import OverviewMonthView from '../../CalculateMoney/views/OverviewMonthView';
import OverviewYearView from '../../CalculateMoney/views/OverviewYearView';
import WeekPicker from '../../CalculateMoney/Pickers/WeekPicker';
import WorkSessionListForDay from '../../CalculateMoney/views/WorkSessionListForDay';
import { useWorkSessionsContext } from '../../../context/WorkSessionsContext';

import {
  boxStyle,
  stackStyle,
  formControlPeriodStyle,
  formControlMonthStyle,
  formControlYearStyle,
  yearSelectSx,
  loadingBoxStyle,
  weekPickerBoxStyle,
  textFieldDayStyle,
  transitionProps,
} from '../../../styles/CalculateMoney/OverviewPanelStyles';

const periodOptions = [
  { label: 'overviewPanel.periodOptions.year', value: 'year' },
  { label: 'overviewPanel.periodOptions.month', value: 'month' },
  { label: 'overviewPanel.periodOptions.week', value: 'week' },
  { label: 'overviewPanel.periodOptions.day', value: 'day' },
  { label: 'overviewPanel.periodOptions.today', value: 'today' }
];

const yearList = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 9 + i);

function toSessionArray(sessions) {
  if (Array.isArray(sessions)) return sessions;
  if (sessions && Array.isArray(sessions.$values)) return sessions.$values;
  return [];
}

const OverviewPanelStaff = ({ businessId, staffId, currentWeek, services, categories }) => {
  const { t } = useTranslation('overviewPanelStaff');

  const {
    staffEarnings,
    staffTodaySummary,
    staffDailyEarnings,
    getStaffYearlyEarnings,
    getStaffMonthlyEarnings,
    getStaffWeeklyEarnings,
    getStaffDailyEarnings,
    getStaffTodaySummary
  } = useCalculateMoneyContext();

  const now = new Date();
  const [period, setPeriod] = useState('today');
  const [selectedDate, setSelectedDate] = useState(format(now, 'yyyy-MM-dd'));
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedYearsBack, setSelectedYearsBack] = useState(5);
  const { workSessions } = useWorkSessionsContext();
  const [showFinishedList, setShowFinishedList] = useState(false);
  const [showFinishedDay, setShowFinishedDay] = useState(false);
  const finishedSessionsToday = getFinishedSessionsFor(format(now, 'yyyy-MM-dd'));

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!staffId) return;
    setLoading(true);

    let promise;
    switch (period) {
      case 'year':
        promise = getStaffYearlyEarnings(businessId, staffId, selectedYearsBack);
        break;
      case 'month':
        promise = getStaffMonthlyEarnings(businessId, staffId, selectedYear);
        break;
      case 'week':
        promise = getStaffWeeklyEarnings(businessId, staffId, selectedYear, selectedWeek);
        break;
      case 'day':
        promise = getStaffDailyEarnings(businessId, staffId, selectedDate);
        break;
      case 'today':
        promise = getStaffTodaySummary(businessId, staffId);
        break;
      default:
        promise = null;
    }

    Promise.resolve(promise).finally(() => setLoading(false));
  }, [
    businessId, staffId, period, selectedDate, selectedWeek, selectedYear, selectedYearsBack,
    getStaffYearlyEarnings, getStaffMonthlyEarnings,
    getStaffWeeklyEarnings, getStaffDailyEarnings, getStaffTodaySummary
  ]);

  function getFinishedSessionsFor(dateString) {
    let sessions = toSessionArray(workSessions);
    return sessions.filter(ws =>
      ws.status?.toLowerCase() === "finished" &&
      ws.sessionStart && ws.sessionStart.slice(0, 10) === dateString
    );
  }

  useEffect(() => {
    setShowFinishedList(false);
  }, [period]);

  useEffect(() => {
    setShowFinishedDay(false);
  }, [period, selectedDate]);

  return (
    <Box sx={boxStyle}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={stackStyle}>
        <FormControl variant="outlined" size="small" sx={formControlPeriodStyle}>
          <InputLabel>{t('overviewPanel.period')}</InputLabel>
          <Select
            label={t('overviewPanel.period')}
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {periodOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {period === 'month' && (
          <FormControl size="small" sx={formControlMonthStyle}>
            <Select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              displayEmpty
              sx={yearSelectSx}
            >
              {yearList.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {period === 'year' && (
          <FormControl size="small" sx={formControlYearStyle}>
            <Select
              value={selectedYearsBack}
              onChange={e => setSelectedYearsBack(Number(e.target.value))}
              displayEmpty
              sx={yearSelectSx}
            >
              {[...Array(10)].map((_, n) => (
                <MenuItem key={n + 1} value={n + 1}>
                  {t('overviewPanel.years', { count: n + 1 })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {period === 'day' && (
          <TextField
            type="date"
            size="small"
            label={t('overviewPanel.pickDate')}
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={textFieldDayStyle}
          />
        )}

        {period === 'week' && (
          <Box sx={weekPickerBoxStyle}>
            <WeekPicker
              year={selectedYear}
              week={selectedWeek}
              onChange={(year, week) => {
                setSelectedYear(year);
                setSelectedWeek(week);
              }}
            />
          </Box>
        )}
      </Stack>

      {loading ? (
        <Box sx={loadingBoxStyle}>
          <CircularProgress />
        </Box>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${period}-${period === 'day'
              ? selectedDate
              : period === 'week'
                ? `${selectedYear}-${selectedWeek}`
                : period === 'month'
                  ? selectedYear
                  : period === 'year'
                    ? selectedYearsBack
                    : ''}`}
            {...transitionProps}
          >
            {period === 'today' && (
              <>
                <OverviewTodayViewStaff data={staffTodaySummary} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('overviewPanel.finishedToday')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowFinishedList(x => !x)}
                    startIcon={showFinishedList ? <RemoveIcon /> : <AddIcon />}
                  >
                    {showFinishedList ? t('buttons.hide') : t('buttons.show')}
                  </Button>
                </Stack>
                {showFinishedList && (
                  <WorkSessionListForDay
                    workSessions={toSessionArray(finishedSessionsToday)}
                    businessServices={services}
                    categories={categories}
                    editable={false}
                  />
                )}
              </>
            )}
            {period === 'day' && (
              <>
                <OverviewDayViewStaff data={staffDailyEarnings?.staffDetails?.$values || []} date={selectedDate} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('overviewPanel.finishedOnDate', { date: selectedDate })}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowFinishedDay(x => !x)}
                    startIcon={showFinishedDay ? <RemoveIcon /> : <AddIcon />}
                  >
                    {showFinishedDay ? t('buttons.hide') : t('buttons.show')}
                  </Button>
                </Stack>
                {showFinishedDay && (
                  <WorkSessionListForDay
                    workSessions={toSessionArray(getFinishedSessionsFor(selectedDate))}
                    businessServices={services}
                    categories={categories}
                    editable={false}
                  />
                )}
              </>
            )}
            {period === 'week' && (<OverviewWeekView data={staffEarnings} week={selectedWeek} year={selectedYear} />)}
            {period === 'month' && (<OverviewMonthView data={staffEarnings || []} year={selectedYear} onYearChange={setSelectedYear} />)}
            {period === 'year' && <OverviewYearView data={staffEarnings} yearsBack={selectedYearsBack} currentYear={new Date().getFullYear()} />}
          </motion.div>
        </AnimatePresence>
      )}
    </Box>
  );
};

export default OverviewPanelStaff;